import {
  Box,
  Button,
  Checkbox,
  DotLoader,
  Flex,
  GenericBanner,
  IconField,
  Input,
  Label,
  Text,
  Tooltip,
} from 'pcln-design-system'
import { ThumbsUp as ThumbsUpIcon, Warning as WarningIcon } from 'pcln-icons'
import React from 'react'
import styled from 'styled-components'
import {
  EMAIL_TYPE,
  SIGNED_UP,
  SIGN_UP_ERROR,
  SIGN_UP_IN_PROGRESS,
  firstNameField,
  formInputField,
  lastNameField,
  termsCheckBox,
} from '../constants'

const FIELD_HEIGHT = '48px'
const SUBSCRIBE_BUTTON_WIDTH = 83

const ButtonBox = styled(Box)`
  white-space: nowrap;
`

const CheckboxLabel = styled(Label)`
  display: flex;
  align-items: center;
`

const SignUpButton = styled(Button)`
  width: 100%;
`

const OptInForm = ({
  agreedToTerms,
  buttonLoadingWidth = SUBSCRIBE_BUTTON_WIDTH,
  buttonText,
  disclaimerTextNode,
  errorMessage,
  failureMessage,
  firstNameError,
  firstNameInput,
  firstNamePlaceholder,
  inputValue,
  lastNameError,
  lastNameInput,
  lastNamePlaceholder,
  placeholderIcon,
  placeholderText,
  showNameFields,
  signUpState,
  successMessage,
  type,
  termsError,
  termsText,
  handleChange,
  handleCheckBoxChange,
  handleSubmit,
}) => {
  const bannerProps = {
    alignItems: 'center',
    borderRadius: 'lg',
    color: 'text',
    justifyContent: 'flex-start',
    mt: 2,
    px: 2,
    py: '12px',
  }
  const signUpErrorBanner = (
    <Box pt={1} width={1}>
      <GenericBanner
        {...bannerProps}
        bg='error.light'
        iconLeft={<WarningIcon color='error' />}
        text={<Text>{failureMessage}</Text>}
      />
    </Box>
  )
  const signUpSuccessBanner = (
    <Box pt={1} width={1}>
      <GenericBanner
        {...bannerProps}
        bg='success.light'
        iconLeft={<ThumbsUpIcon color='success' />}
        text={<Text>{successMessage}</Text>}
      />
    </Box>
  )

  return (
    <Box>
      {signUpState === SIGNED_UP && signUpSuccessBanner}
      {signUpState === SIGN_UP_ERROR && signUpErrorBanner}
      {signUpState !== SIGNED_UP && (
        <>
          {termsText && (
            <Box mt={2}>
              <CheckboxLabel textStyle='disclaimer'>
                <Checkbox
                  id={termsCheckBox}
                  data-testid={termsCheckBox}
                  color={termsError ? 'error' : 'primary'}
                  checked={agreedToTerms}
                  onChange={handleCheckBoxChange}
                />{' '}
                <Text.span
                  data-testid='terms-text'
                  color={termsError ? 'error' : 'text.light'}
                  textStyle='disclaimer'
                >
                  {termsText}
                </Text.span>
              </CheckboxLabel>
              {termsError && (
                <Tooltip bottom left color='error' zIndex='1'>
                  <Text>Please agree to the terms and conditions</Text>
                </Tooltip>
              )}
            </Box>
          )}
          {showNameFields && type === EMAIL_TYPE && (
            <Flex flexDirection={['column', null, 'row']} pt={termsText ? 1 : 2} mt={1}>
              <Box width={[1, null, 1 / 2]} pr={[null, null, 2]} pb={[2, null, 1]}>
                <IconField>
                  <Input
                    data-testid={firstNameField}
                    id={firstNameField}
                    name={firstNameField}
                    borderRadius='lg'
                    color={firstNameError ? 'error' : undefined}
                    height={FIELD_HEIGHT}
                    placeholder={firstNamePlaceholder || 'First name'}
                    value={firstNameInput}
                    onChange={handleChange}
                  />
                  {firstNameError !== '' && <WarningIcon color='error' />}
                </IconField>
                {firstNameError && (
                  <Tooltip bottom right color='error' zIndex='1'>
                    <Text>{firstNameError}</Text>
                  </Tooltip>
                )}
              </Box>
              <Box width={[1, null, 1 / 2]} ml={[null, null, 1]} pt={[1, null, 0]}>
                <IconField>
                  <Input
                    data-testid={lastNameField}
                    id={lastNameField}
                    name={lastNameField}
                    borderRadius='lg'
                    color={lastNameError ? 'error' : undefined}
                    height={FIELD_HEIGHT}
                    placeholder={lastNamePlaceholder || 'Last name'}
                    value={lastNameInput}
                    onChange={handleChange}
                  />
                  {lastNameError !== '' && <WarningIcon color='error' />}
                </IconField>
                {lastNameError && (
                  <Tooltip bottom right color='error' zIndex='1'>
                    <Text>{lastNameError}</Text>
                  </Tooltip>
                )}
              </Box>
            </Flex>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmit()
            }}
          >
            <Flex
              flexDirection={['column', null, null, 'row']}
              justifyContent='space-between'
              mt={1}
              pt={showNameFields ? [2, null, 1] : termsText ? 1 : 2}
              width={1}
            >
              <Box mr={[0, null, null, 1]} pr={[0, null, null, 2]} width={1}>
                <IconField>
                  {placeholderIcon}
                  <Input
                    data-testid={formInputField}
                    id={formInputField}
                    name={formInputField}
                    borderRadius='lg'
                    color={errorMessage ? 'error' : undefined}
                    height={FIELD_HEIGHT}
                    placeholder={placeholderText}
                    value={inputValue}
                    onChange={handleChange}
                  />
                  {errorMessage !== '' && <WarningIcon color='error' />}
                </IconField>
                {errorMessage && (
                  <Tooltip bottom right color='error' zIndex='1'>
                    <Text>{errorMessage}</Text>
                  </Tooltip>
                )}
              </Box>
              <ButtonBox mt={[1, null, null, 0]} pt={[2, null, null, 0]}>
                <SignUpButton
                  data-testid='form-button'
                  borderRadius='action-lg'
                  disabled={signUpState === SIGN_UP_IN_PROGRESS}
                  height='100%'
                  size='large'
                >
                  {signUpState === SIGN_UP_IN_PROGRESS ? (
                    <Flex data-testid='dot-loader' justifyContent='space-around' width={buttonLoadingWidth}>
                      <DotLoader color='text.light' />
                    </Flex>
                  ) : (
                    buttonText
                  )}
                </SignUpButton>
              </ButtonBox>
            </Flex>
          </form>
        </>
      )}
      {disclaimerTextNode && (
        <Box mt={1} pt={2}>
          {disclaimerTextNode}
        </Box>
      )}
    </Box>
  )
}

export default OptInForm
